import { useContext, useEffect, useState } from 'react';
import UserContext from '../../../../contexts/UserContext';
import { getRequest } from '../../../../helpers/httpHandler2';
import { isSignedIn } from '../../publicView';

/*
  Which site-menu items (Page Builder "Upgraded Navbar") a viewer sees. Shared by
  the desktop menu (DesktopNavItems, DesktopSubItems) and the phone menu
  (NavItemsTray), so the two can no longer disagree. Until 10/2026 the phone
  menu checked nothing and showed every link to everyone (TECHNICAL_DEBT.md
  BUG-064, workspace root).

  COACHING_PARTNER_BOARD_URL is a reserved address. Rulesets match Keap tags or a
  list of users and cannot say "is somebody's coaching partner", so a menu item
  with this URL is shown only to coaching sub-partners, whatever its ruleset,
  and Administrators do not see it either. Spec:
  tasks/cwu_coaching_partner/SPEC.md section 7.2 "How the link is stored and
  shown" (workspace root).
*/
export const COACHING_PARTNER_BOARD_URL = '/boards/coaching/partner';

export type NavPermission = {
  admin_role_id: number;
  name: string;
};

type MenuItem = {
  url: string;
  rule_set: number;
};

export type NavVisibility = {
  ruleAccess: { [id: number]: boolean } | undefined;
  editMode: boolean;
  isAdmin: boolean;
  isCoachingSubPartner: boolean;
};

export const isNavItemVisible = (item: MenuItem, viewer: NavVisibility) => {
  if (viewer.editMode) return true;
  if (item.url === COACHING_PARTNER_BOARD_URL)
    return viewer.isCoachingSubPartner;
  if (viewer.isAdmin) return true;
  if (!viewer.ruleAccess) return false;
  if (!item.rule_set) return true;
  if (item.rule_set === -1) return true;
  return !!viewer.ruleAccess[item.rule_set];
};

export const isAdministrator = (userBundle: any) => {
  const permissions: NavPermission[] = Object.values(
    userBundle?.permissions || {}
  );
  return permissions.some((permission) => permission.name === 'Administrator');
};

// One request per signed-in user per page load, however many menu pieces ask.
let partnerCheck: { userId: number; result: Promise<boolean> } | null = null;

export const resetCoachingPartnerCheck = () => {
  partnerCheck = null;
};

const checkIsCoachingSubPartner = (userId: number) => {
  if (!partnerCheck || partnerCheck.userId !== userId) {
    partnerCheck = {
      userId,
      result: getRequest('/api/coaching-partners/me')
        .then((mine) => mine?.role === 'partner')
        .catch(() => false)
    };
  }
  return partnerCheck.result;
};

export const useIsCoachingSubPartner = () => {
  const { user_bundle } = useContext(UserContext);
  const userId: number | undefined = user_bundle?.user?.id;
  const [isSubPartner, setIsSubPartner] = useState(false);

  useEffect(() => {
    // Never ask while signed out: the menu also shows on public pages, and a 401
    // from httpHandler2 sends the visitor to /login.
    if (!userId || !isSignedIn()) {
      setIsSubPartner(false);
      return;
    }
    let active = true;
    checkIsCoachingSubPartner(userId).then((result) => {
      if (active) setIsSubPartner(result);
    });
    return () => {
      active = false;
    };
  }, [userId]);

  return isSubPartner;
};

// Everything a menu needs to decide visibility, from one place.
export const useNavVisibility = (
  ruleAccess: NavVisibility['ruleAccess'],
  editMode: boolean
): NavVisibility => {
  const { user_bundle } = useContext(UserContext);
  const isCoachingSubPartner = useIsCoachingSubPartner();
  return {
    ruleAccess,
    editMode,
    isAdmin: isAdministrator(user_bundle),
    isCoachingSubPartner
  };
};
